<template>
  <form @submit.prevent="submit" class="mf-form">
    <div class="mf-warn">
      <div class="warn-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 9v4M12 17h.01" stroke-linecap="round" />
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="warn-copy">
        <div class="warn-title">Mark this pay-in as FAILED?</div>
        <div v-if="orderRow.paymentStatus === 'approved'" class="warn-body">
          This order is currently <strong>approved</strong>. Marking it failed will:
          <ul>
            <li>Reverse <strong>₹{{ formatAmount(amountInPlay) }}</strong> + commission from the bank's balance / commission / payInLimit.</li>
            <li>Flip the order's status to <strong>failed</strong>.</li>
            <li>Fire the failed-order callback to the merchant.</li>
          </ul>
          <div class="warn-final">This cannot be undone.</div>
        </div>
        <div v-else class="warn-body">
          Flipping <strong class="upper">{{ orderRow.paymentStatus }}</strong> → <strong>failed</strong> and firing the merchant's failed-order callback. No balance change (no money was credited).
        </div>
      </div>
    </div>

    <div class="mf-summary">
      <div class="sum-row">
        <span class="sum-label">Order</span>
        <span class="sum-value">{{ orderRow.merchantOrderId || orderRow.refID }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Status</span>
        <span class="sum-value upper">{{ orderRow.paymentStatus }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Amount</span>
        <span class="sum-value">₹{{ formatAmount(amountInPlay) }}</span>
      </div>
      <div v-if="orderRow.transactionID" class="sum-row">
        <span class="sum-label">UTR</span>
        <span class="sum-value mono break">{{ orderRow.transactionID }}</span>
      </div>
    </div>

    <div class="field">
      <label>Reason <span class="req">*</span></label>
      <textarea
        v-model.trim="reason"
        rows="3"
        maxlength="500"
        class="textarea"
        placeholder="Why is this order being marked failed?"
        autofocus
      ></textarea>
      <div class="field-foot">
        <span class="field-hint">Stored in the audit log.</span>
        <span class="char-count">{{ reason.length }}/500</span>
      </div>
    </div>

    <p v-if="errorMsg" class="form-alert">{{ errorMsg }}</p>

    <div class="form-actions">
      <button
        type="button"
        class="btn btn-secondary"
        @click="$emit('close')"
        :disabled="isSubmitting"
      >Cancel</button>
      <button
        type="submit"
        class="btn btn-danger"
        :disabled="isSubmitting || !reason.trim()"
      >
        <span v-if="!isSubmitting">Mark failed</span>
        <span v-else class="btn-loading">
          <span class="spinner-mini"></span>
          Working…
        </span>
      </button>
    </div>
  </form>
</template>

<script>
export default {
  name: "mark-failed-form",
  props: {
    orderRow: { type: Object, required: true },
  },
  emits: ["close", "failed"],
  data() {
    return {
      reason: "",
      errorMsg: null,
      isSubmitting: false,
    };
  },
  computed: {
    amountInPlay() {
      const ap = parseFloat(this.orderRow.approved_amount || 0);
      return ap > 0 ? ap : parseFloat(this.orderRow.amount || 0);
    },
  },
  methods: {
    formatAmount(n) {
      const v = parseFloat(n || 0);
      return Number.isFinite(v) ? v.toFixed(2) : '0.00';
    },
    async submit() {
      this.errorMsg = null;
      if (!this.reason || !this.reason.trim()) {
        this.errorMsg = "A reason is required.";
        return;
      }
      this.isSubmitting = true;
      try {
        await this.$store.dispatch("payin/markPayInFailed", {
          refID: this.orderRow.refID,
          reason: this.reason.trim(),
        });
        this.$emit("failed", "Order marked failed. Merchant has been notified.");
      } catch (e) {
        this.errorMsg = e.message || "Failed to mark order failed.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.mf-form { display: flex; flex-direction: column; gap: 14px; }

.mf-warn {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #7f1d1d;
}
.warn-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: #fee2e2;
  color: #dc2626;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.warn-icon svg { width: 18px; height: 18px; }
.warn-copy { flex: 1; min-width: 0; font-size: 12.5px; line-height: 1.55; }
.warn-title { font-weight: 700; font-size: 13px; margin-bottom: 4px; color: #991b1b; }
.warn-body ul { list-style: disc; margin: 6px 0 0; padding-left: 18px; }
.warn-body li { margin-bottom: 2px; }
.warn-final { margin-top: 8px; font-weight: 700; color: #991b1b; }
.upper { text-transform: uppercase; letter-spacing: 0.03em; }

.mf-summary {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sum-row {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; }
.sum-value.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; color: #334155; }
.sum-value.break { word-break: break-all; overflow-wrap: anywhere; }

.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.req { color: #dc2626; }
.textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.textarea:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.field-foot {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 6px;
  font-size: 11px;
}
.field-hint { color: #94a3b8; }
.char-count { color: #94a3b8; font-variant-numeric: tabular-nums; }

.form-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  margin: 0;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-danger {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(220, 38, 38, 0.45);
}
.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(220, 38, 38, 0.55);
}
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
